// Routing for the auto-input box. A paste can be one of three things:
//   1. a single identifier / URL  → the existing single-citation path (no regression),
//   2. a BibTeX library blob       → also the single path (Citation.js parses multi-entry
//      `.bib` itself), even if it spans many lines, and
//   3. a newline-separated LIST of identifiers (DOIs / ISBNs / PMIDs / arXiv / URLs) →
//      bulk paste: each line is resolved independently and added to the bibliography.
//
// This module is intentionally pure (no React, no network) so the dispatch logic can be
// unit-tested without hitting the API.

// A pasted BibTeX entry starts with `@type{` — keep the WHOLE blob together and let the
// existing BibTeX path parse it, instead of splitting it line-by-line.
const BIBTEX_BLOB_REGEX = /^@\w+\{/;
const LINE_SPLIT_REGEX = /\r?\n/;

// Upper bound on a single bulk paste. Caps the number of sequential upstream lookups so a
// huge paste can't hammer Crossref/PubMed/OpenLibrary (which already rate-limit at 429).
export const BULK_INPUT_CAP = 25;

export type AutoInputPlan =
  | { kind: "single"; input: string }
  | { kind: "bulk"; lines: string[]; droppedCount: number };

/**
 * Decide how to handle the raw auto-input value.
 *
 * - Empty input → a single (empty) plan; the caller already guards against blanks.
 * - A leading `@type{` → single path (BibTeX blob, possibly multi-line/multi-entry).
 * - 0 or 1 non-empty line → single path (behaves exactly as before).
 * - 2+ non-empty lines → bulk path; lines beyond {@link BULK_INPUT_CAP} are reported via
 *   `droppedCount` so the UI can tell the user what was skipped.
 */
export function planAutoInput(raw: string): AutoInputPlan {
  const trimmed = raw.trim();

  if (BIBTEX_BLOB_REGEX.test(trimmed)) {
    return { kind: "single", input: trimmed };
  }

  const lines = trimmed
    .split(LINE_SPLIT_REGEX)
    .map((line) => line.trim())
    .filter((line) => line.length > 0);

  if (lines.length <= 1) {
    return { kind: "single", input: trimmed };
  }

  const capped = lines.slice(0, BULK_INPUT_CAP);
  return {
    kind: "bulk",
    lines: capped,
    droppedCount: lines.length - capped.length,
  };
}
